import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { EditableProcedureData } from "../../../../types/editableTreatmentPlan";
import ToothSelectionSelect from "../../../../components/ToothSelectionSelect";
import { TreatmentStepEditChips } from "../TreatmentStepChips";
import { EditAddCodeButtonWrapper } from "./EditTreatmentAppointment.styled";
import { getToothCount } from "../../../../utils/toothSurfacesUtils";
import {
  allowsRangeToothTab,
  allowsSingleToothTab,
  canSelectTeeth,
  getMaxTeethAllowed,
  hasValidToothCount,
} from "../../../../utils/treatAreaCapabilities";

export const MAX_TEETH_PER_STEP = 8;

export interface EditProcedureToothFieldProps {
  appointmentId: number;
  procedureIndex: number;
  procedure: EditableProcedureData;
  onChange: (teeth: string[]) => void;
}

const EditProcedureToothField: React.FC<EditProcedureToothFieldProps> = ({
  appointmentId,
  procedureIndex,
  procedure,
  onChange,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const hasCdtCode = procedure.resolvedCdtCodes.length > 0;
  const capabilities = procedure.treatAreaCapabilities;
  const toothSelectionAllowed = canSelectTeeth(capabilities);
  const maxTeeth = getMaxTeethAllowed(capabilities, MAX_TEETH_PER_STEP);
  const toothCount = getToothCount(procedure.teeth);
  const isDisabled = !hasCdtCode || !toothSelectionAllowed;
  const disabledReason = !hasCdtCode
    ? t("treatmentPlan.selectCdtCodeFirst")
    : t("treatmentPlan.cannotSelectWithTheseCodes");

  const teethValid = hasValidToothCount(procedure.teeth, capabilities);
  const showEmptyRequired =
    hasCdtCode && toothSelectionAllowed && toothCount === 0;
  // needsRange without needsTooth: exactly 1 tooth is an invalid intermediate state
  const showInsufficientTeethError =
    hasCdtCode &&
    toothSelectionAllowed &&
    !teethValid &&
    toothCount > 0;

  const handleRemove = (value: string) => {
    if (isDisabled) return;
    onChange(procedure.teeth.filter((tooth) => tooth !== value));
  };

  const handleChange = (teeth: string[]) => {
    onChange(teeth.slice(0, maxTeeth));
  };

  const allowSingle = allowsSingleToothTab(capabilities);
  const allowRange = allowsRangeToothTab(capabilities);

  return (
    <EditAddCodeButtonWrapper>
      <TreatmentStepEditChips
        values={procedure.teeth}
        onRemove={handleRemove}
        onAddClick={() => setOpen(true)}
        disabled={isDisabled}
        disabledReason={disabledReason}
        error={showEmptyRequired}
        errorReason={t("treatmentPlan.valueRequired")}
        valuesError={showInsufficientTeethError}
        valuesErrorReason={t("treatmentPlan.moreThanOneToothRequired")}
      />
      <ToothSelectionSelect
        hideTrigger
        open={open && !isDisabled}
        onOpenChange={(nextOpen) => {
          if (isDisabled) {
            setOpen(false);
            return;
          }
          setOpen(nextOpen);
        }}
        name={`tooth-${appointmentId}-${procedureIndex}`}
        value={procedure.teeth}
        onChange={handleChange}
        maxValues={maxTeeth}
        allowSingle={allowSingle}
        allowRange={allowRange}
      />
    </EditAddCodeButtonWrapper>
  );
};

export default EditProcedureToothField;
